import { CheckIcon, EditIcon, PenIcon, PlusIcon, TicketIcon, TrashIcon } from "@xola/icons";
import { Alert, Badge, Button, flash, SubmitButton, Tooltip } from "@xola/ui-kit";
import pluralize from "pluralize";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { CatalogItem, Experience, UnitType } from "types";
import { api } from "../../../../api/api";
import { useAutoFetchCollection } from "../../../../hooks/useAutoFetchCollection";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { isDefaultDemographic } from "../../../../models/DemographicUnit";
import { HeaderToolBarActions } from "../../../Common/HeaderToolbarActions";
import { InlineConfirmDeletePopover } from "../../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../../Common/NoTranslate";
import { ContentLayout } from "../../../Layout/ContentLayout";
import { Loader } from "../../../Loader";
import { CreateCatalogItemModal } from "../../pricing/CreateCatalogItemModal";
import { DeleteCatalogItemModal, DeleteConflictError } from "../DeleteCatalogItemModal";
import { EditGlobalDemographicModal } from "./EditGlobalDemographicModal";
import { useCatalogItems } from "@/hooks/resources/useCatalogItems";

const DemographicsExperienceList = ({
    experiences,
    catalogItem,
}: {
    experiences: Experience[];
    catalogItem: CatalogItem;
}) => {
    if (!experiences) {
        return null;
    }

    return (
        <ul className="space-y-1 p-2" data-testid="demographics-listing-label">
            {experiences.map((experience) => {
                const catalogItemCurrent = experience.catalog?.items?.find(
                    (item) => item.parent?.id === catalogItem.id,
                );

                return (
                    <li key={experience.id}>
                        <NoTranslate>
                            {catalogItemCurrent?.overrideParent ? (
                                <>
                                    <EditIcon /> {experience.name}
                                </>
                            ) : (
                                <>
                                    <CheckIcon /> {experience.name}
                                </>
                            )}
                        </NoTranslate>
                    </li>
                );
            })}
        </ul>
    );
};

export const GlobalDemographics = () => {
    const { data: demographicCatalogItems, isLoading: isLoadingCatalogItems } = useCatalogItems();

    const { data: experiences, isLoading: isLoadingExperiences } = useAutoFetchCollection<Experience>("experiences");

    const isLoading = isLoadingCatalogItems || isLoadingExperiences;
    const catalogItemExperienceMap: { [key: ID]: ID[] } = isLoading
        ? {}
        : getCatalogItemExperienceMap(demographicCatalogItems, experiences);

    const [showCreateCatalogItemModal, setShowCreateCatalogItemModal] = useState(false);
    const toggleCreateCatalogItemModal = () => {
        setShowCreateCatalogItemModal(!showCreateCatalogItemModal);
    };

    return (
        <ContentLayout isWide>
            <HeaderToolBarActions>
                <Button color="primary" icon={<PlusIcon />} onClick={toggleCreateCatalogItemModal}>
                    Create Demographic
                </Button>
            </HeaderToolBarActions>

            <CreateCatalogItemModal
                isOpen={showCreateCatalogItemModal}
                onCreate={toggleCreateCatalogItemModal}
                onClose={toggleCreateCatalogItemModal}
            />
            <Alert className="mt-5" color="primary">
                Global demographics are guest types that are common across your products.
            </Alert>
            <div>
                {isLoading ? (
                    <Loader size="tiny" className="p-2" text="Loading Demographics" />
                ) : demographicCatalogItems.length > 0 ? (
                    <div className="divide-y divide-gray-light">
                        {demographicCatalogItems.map((catalogItem: CatalogItem) => (
                            <DemographicCatalogItem
                                key={catalogItem.id}
                                catalogItemExperienceMap={catalogItemExperienceMap}
                                catalogItem={catalogItem}
                            />
                        ))}
                    </div>
                ) : (
                    <p>Demographics not found</p>
                )}
            </div>
        </ContentLayout>
    );
};

const DemographicCatalogItem = ({
    catalogItem,
    catalogItemExperienceMap,
}: {
    catalogItem: CatalogItem;
    catalogItemExperienceMap: { [key: ID]: ID[] };
}) => {
    const invalidate = useInvalidate();
    const catalogItemExperiences =
        catalogItem.id && catalogItemExperienceMap[catalogItem.id]
            ? ([...catalogItemExperienceMap[catalogItem.id]] as unknown as Experience[])
            : [];
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showEditCatalogItemModal, setShowEditCatalogItemModal] = useState(false);
    const [deleteConflictError, setDeleteConflictError] = useState<DeleteConflictError>();

    const toggleConfirmDeleteModal = () => {
        setShowConfirmDeleteModal(!showConfirmDeleteModal);
        setDeleteConflictError(undefined);
    };

    const toggleEditCatalogItemModal = () => {
        setShowEditCatalogItemModal(!showEditCatalogItemModal);
    };

    const { mutateAsync: deleteCatalogItem, isLoading } = useMutation(
        async ({ id, cascade }: { id: ID; cascade: boolean }) => {
            const url = `/catalogItems/${id}${cascade ? "?cascade=true" : ""}`;
            return api.delete(url);
        },
    );

    const handleDeleteCatalogItem = async (cascade = false) => {
        try {
            await deleteCatalogItem({ id: catalogItem.id!, cascade });
            flash.show({ text: `Successfully removed demographic`, color: "success" });
            invalidate("catalogItems", catalogItem.id);
        } catch (error: any) {
            const deleteError = error?.response?.data?.field ?? {};
            const { reason, conflictingData, message } = deleteError;
            setDeleteConflictError({ reason, conflictingData, message });
            setShowConfirmDeleteModal(true);
        }
    };

    return (
        <div className="flex items-center justify-between py-6" data-testid="global-demographics-item">
            <NoTranslate if={!isDefaultDemographic(catalogItem.name)} as="h4" className="font-bold">
                {catalogItem.name}
            </NoTranslate>
            <div className="flex items-center space-x-6">
                <div>
                    <Tooltip
                        content={
                            <DemographicsExperienceList
                                experiences={catalogItemExperiences}
                                catalogItem={catalogItem}
                            />
                        }
                    >
                        <Badge color="secondary" icon={<TicketIcon />} size="medium">
                            <div>
                                <var data-var="productCount">{catalogItemExperiences.length}</var>{" "}
                                {pluralize("product", catalogItemExperiences.length)}
                            </div>
                        </Badge>
                    </Tooltip>
                </div>
                <div className="flex items-center space-x-1">
                    <Button
                        variant="outline"
                        color="secondary"
                        size="small"
                        data-testid="global-demographics-edit-button"
                        onClick={toggleEditCatalogItemModal}
                    >
                        <PenIcon />
                    </Button>
                    <InlineConfirmDeletePopover offset={[0, 20]} onConfirm={async () => handleDeleteCatalogItem()}>
                        <SubmitButton
                            data-testid="global-demographics-remove-button"
                            isLoading={isLoading}
                            variant="outline"
                            color="secondary"
                            size="small"
                        >
                            <TrashIcon />
                        </SubmitButton>
                    </InlineConfirmDeletePopover>
                    <DeleteCatalogItemModal
                        isOpen={showConfirmDeleteModal}
                        unitType={UnitType.DEMOGRAPHIC}
                        conflictError={deleteConflictError}
                        onClose={toggleConfirmDeleteModal}
                        onDelete={async () => handleDeleteCatalogItem(true)}
                    />
                    <EditGlobalDemographicModal
                        catalogItem={catalogItem}
                        experiences={catalogItemExperiences}
                        isOpen={showEditCatalogItemModal}
                        onClose={toggleEditCatalogItemModal}
                    />
                </div>
            </div>
        </div>
    );
};

/**
 * This function will create a map of all experience ids for a catalog item.
 */
const getCatalogItemExperienceMap = (catalogItems: CatalogItem[], experiences: Experience[]): { [key: ID]: ID[] } => {
    const catalogItemExperienceMap: any = {};
    for (const catalogItem of catalogItems) {
        catalogItemExperienceMap[catalogItem.id!] = new Set();
    }

    for (const experience of experiences) {
        for (const catalogItem of experience.catalog.items) {
            if (catalogItem.parent?.id && catalogItemExperienceMap[catalogItem.parent.id]) {
                catalogItemExperienceMap[catalogItem.parent.id].add(experience);
            }
        }
    }

    return catalogItemExperienceMap;
};
